import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { Helmet } from "react-helmet";
import { RouteComponentProps, useLocation } from "react-router";
import Button from "../../../components/Button";
import Error from "../../../components/Error";
import Info from "../../../components/Info";
import Loading from "../../../components/Loading";
import WeduTooltip from "../../../components/WeduTooltip/intex";
import { useWizard } from "../../wizard/context";
import { AcademicHigher } from "../components/AcademicHigher";
import AcademicsFilterFormHigher from "../components/AcademicsFilterFormHigher";
import AcademicsFilter from "../components/AcademicsFilter";
import { useAcademicsPageHigher } from "../hooks/useAcademicsPageHigher";
import { useAcademicsPageStyles } from "../styles/useAcademicsPageStyles";
import { IAcademicsContext } from "../types";
import { endOfTitle } from "../../../constants/constants";
import { useCheckForViewModal } from "../hooks/useCheckForViewModal";
import queryString from "querystring";
import useAcademicQueriesHigher from "../hooks/useAcademicQueriesHigher";
import { useSchoolId } from "../../school/hooks/useSchoolId";
import { CircularProgress } from "@material-ui/core";
import { useUserAccount } from "../../user/userAccount/context";
import { useAuth } from "../../auth/hooks/useAuth";
import { UserRole } from "../../user/enums/UserRole";
import { useNotify } from "../../notify/useNotify";
import { NotifyMode } from "../../notify/NotifyModeEnum";
import { mutateErrors } from "../../../utils";
import { AiConfirmDialog } from "../modals/components/AiConfirmDialog";

const AcademicsHigherContext = createContext({} as IAcademicsContext);
export const useAcademicsHigherContext = (): IAcademicsContext =>
  useContext(AcademicsHigherContext);

const AcademicsPageHigher: FC<RouteComponentProps> = () => {
  const classes = useAcademicsPageStyles();
  const { openWizard } = useWizard();
  const snackNotify = useNotify();
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [highlightedAcademics, setHighlightedAcademics] = useState<string[]>(
    [],
  );
  const schoolId = useSchoolId();
  const {
    user,
    profile: { data: { data: profile = null } = {} },
  } = useAuth();
  const { profile: accountProfile } = useUserAccount();

  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);

  const hasAiPendingRequest = isAdminOrSupport
    ? accountProfile?.data.data.school?.hasAiPendingRequest
    : profile?.school?.hasAiPendingRequest;

  const [isAiPending, setIsAiPending] = useState<boolean>(hasAiPendingRequest);

  const { addWithAI } = useAcademicQueriesHigher();
  const {
    isLoading,
    isError,
    errorMessage,
    applyFilter,
    academics,
    refetchAcademics,
    academicWizardConfigs,
    programWizardConfigs,
    intakeWizardConfigs,
  } = useAcademicsPageHigher();

  useCheckForViewModal({
    getViewAcademicConfig: academicWizardConfigs.getViewConfig,
    getViewProgramConfig: programWizardConfigs.getViewConfig,
  });

  const submitHandler = useCallback((data) => {
    setIsFilterApplied(true);
    applyFilter(data);
  }, []);

  const contextValue = useRef<IAcademicsContext>({
    academicWizardConfigs,
    programWizardConfigs,
    intakeWizardConfigs,
    refetchAcademics,
    submitHandler,
  });

  const addAcademic = useCallback(async () => {
    (await openWizard(academicWizardConfigs.getCreateConfig())) &&
      (await refetchAcademics());
  }, []);

  const handleAddWithAI = useCallback(async () => {
    const isConfirmed = await openWizard({
      steps: [
        {
          width: "550px",
          titleConfig: {
            titleText: "Program Automatic Creation",
          },
          content: AiConfirmDialog,
        },
      ],
      submitButtonText: "Proceed",
      cancelButtonText: "Cancel",
    });

    if (!isConfirmed) {
      return;
    }

    setIsAiPending(true);
    try {
      await addWithAI(schoolId);
    } catch (error) {
      const { message } = mutateErrors(error);
      snackNotify("Error", message, NotifyMode.ERROR);
      setIsAiPending(false);
    }
  }, [addWithAI, schoolId]);

  const location = useLocation();

  useEffect(() => {
    const queryParams = queryString.parse(
      location.search.split("?").join(""),
      " ",
    );

    if (typeof queryParams.highlightedAcademics === "string") {
      setHighlightedAcademics(queryParams.highlightedAcademics.split(","));
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Academics{endOfTitle}</title>
      </Helmet>
      <AcademicsHigherContext.Provider value={contextValue.current}>
        {isLoading && <Loading />}
        <Container maxWidth={"lg"} className={classes.root}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <div className={classes.stickyContainer}>
                <AcademicsFilter
                  submitHandler={submitHandler}
                  filterForm={AcademicsFilterFormHigher}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={9}>
              {!isLoading && (
                <>
                  <Container maxWidth="lg" className={classes.buttonContainer}>
                    <WeduTooltip
                      title="Fill programs with AI"
                      className={classes.button}
                    >
                      <Button
                        color={"success"}
                        variant={"contained"}
                        onClick={handleAddWithAI}
                        disabled={isAiPending}
                        startIcon={
                          isAiPending && (
                            <CircularProgress size={15} />
                          )
                        }
                      >
                        Add With AI
                      </Button>
                    </WeduTooltip>
                    <WeduTooltip
                      title="Add academic"
                      className={classes.button}
                    >
                      <Button
                        color={"success"}
                        variant={"contained"}
                        onClick={addAcademic}
                      >
                        Add Manually
                      </Button>
                    </WeduTooltip>
                  </Container>
                  {isError ? (
                    <Error text={errorMessage} />
                  ) : academics.length > 0 || isFilterApplied ? (
                    <Container maxWidth="lg" className={classes.container}>
                      {academics.length > 0 ? (
                        <div className={classes.listContainer}>
                          {academics.map((academic) => (
                            <AcademicHigher
                              key={academic.id}
                              academic={academic}
                              highlighted={highlightedAcademics.includes(
                                academic.id,
                              )}
                            />
                          ))}
                        </div>
                      ) : (
                        <Info
                          className={classes.info}
                          icon={"search"}
                          text={"You have no results for now."}
                        />
                      )}
                    </Container>
                  ) : (
                    <Container maxWidth="lg" className={classes.container}>
                      <Info
                        className={classes.info}
                        text={
                          "Step 1: Create Branches.\nStep 2: Complete Academics and assign to branches."
                        }
                      />
                    </Container>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      </AcademicsHigherContext.Provider>
    </>
  );
};

export default AcademicsPageHigher;
